import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Globe, Mail, Phone, MapPin, 
  ExternalLink, Building2, MessageCircle
} from 'lucide-react';

function SupportPage() {
  const location = useLocation();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-silver-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-header" data-testid="nav-header">
        <div className="container-wide py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold tracking-tight">
                <span className="text-slate-900">innovator</span>
                <span className="text-gradient-red">foundervisa</span>
              </span>
            </Link>
            
            <Link to="/">
              <Button variant="ghost" className="btn-ghost">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-wide">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="w-20 h-20 rounded-2xl gradient-red flex items-center justify-center mx-auto mb-6 shadow-lg shadow-red-500/25">
              <MessageCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-6">
              Get in <span className="text-gradient-red">Touch</span>
            </h1>
            <p className="text-lg text-slate-600">
              Have questions about the Innovator Founder Visa? We're here to help you on your journey to the UK.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-16">
        <div className="container-wide">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Email Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-6">
                <Mail className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Email Us</h3>
              <p className="text-slate-500 mb-4">For general inquiries and support</p>
              <a 
                href="mailto:support@innovatorfoundervisas.com"
                className="text-red-500 font-semibold hover:text-red-600 transition-colors inline-flex items-center gap-2 text-sm break-all"
              >
                <span className="break-all">support@innovatorfoundervisas.com</span>
                <ExternalLink className="w-4 h-4 flex-shrink-0" />
              </a>
            </motion.div>

            {/* WhatsApp Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow"
            >
              <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center mx-auto mb-6">
                <Phone className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">WhatsApp</h3>
              <p className="text-slate-500 mb-4">Quick responses via WhatsApp</p>
              <a 
                href="https://wa.me/447380885506"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-500 font-semibold hover:text-green-600 transition-colors inline-flex items-center gap-2"
              >
                +44 7380 885506
                <ExternalLink className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Location Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow md:col-span-2 lg:col-span-1"
            >
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-6">
                <MapPin className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Location</h3>
              <p className="text-slate-500 mb-4">Based in the United Kingdom</p>
              <p className="text-slate-700 font-medium">
                Glasgow, Scotland<br />
                United Kingdom
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Website Section */}
      <section className="py-16 bg-slate-50">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-100">
              <Globe className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-900 mb-2">Our Website</h3>
              <a 
                href="https://www.innovatorfoundervisas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-red-500 font-semibold hover:text-red-600 transition-colors inline-flex items-center gap-2 text-lg"
              >
                www.innovatorfoundervisas.com
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Developer Credit Section */}
      <section className="py-16">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <a 
              href="https://www.cloudlabweb.com"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 text-center text-white shadow-xl hover:shadow-2xl transition-all hover:scale-[1.02] cursor-pointer"
            >
              <Building2 className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">System Developed By</p>
              <h3 className="text-2xl font-bold mb-2 flex items-center justify-center gap-2">
                Cloud Lab Technologies
                <ExternalLink className="w-5 h-5 text-red-400" />
              </h3>
              <p className="text-slate-400">
                <MapPin className="w-4 h-4 inline mr-1" />
                United Kingdom
              </p>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container-wide">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-red flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold">innovatorfoundervisas.com</span>
            </div>
            <p className="text-slate-400 text-sm text-center">
              © 2026 innovatorfoundervisas.com. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default SupportPage;
